import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

/**
 * SectionTabs — logica/UX condivisa della pagina Metodo, riutilizzabile.
 * Desktop: elenco a sinistra (voce = badge + titolo + sottotitolo), pannello a destra.
 * Mobile: accordion, il contenuto della voce attiva si espande sotto la voce.
 * Solo il contenuto della voce attiva è nel DOM (come nella pagina Metodo).
 *
 * items: [{ id, badge (nodo o stringa), title, subtitle?, render: () => JSX }]
 */
export default function SectionTabs({ items = [], ariaLabel = "Sezioni", testIdPrefix = "sectiontab" }) {
  const [active, setActive] = useState(0);
  const btnRefs = useRef([]);

  useEffect(() => {
    // Se la lista cambia (es. cambio pagina), riparti dalla prima voce.
    setActive(0);
  }, [items.length, ariaLabel]);

  if (!items || items.length === 0) return null;
  const activeItem = items[Math.min(active, items.length - 1)];

  const focusTab = (idx) => {
    setActive(idx);
    btnRefs.current[idx]?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      focusTab((active + 1) % items.length);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      focusTab((active - 1 + items.length) % items.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusTab(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusTab(items.length - 1);
    }
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6">
      {/* Elenco voci (con accordion inline su mobile) */}
      <div role="tablist" aria-label={ariaLabel} aria-orientation="vertical" onKeyDown={onKeyDown}>
        {items.map((it, idx) => {
          const isActive = active === idx;
          return (
            <div key={it.id ?? idx} className="border-b border-ice/8 last:border-b-0">
              <button
                ref={(el) => {
                  btnRefs.current[idx] = el;
                }}
                role="tab"
                id={`${testIdPrefix}-tab-${idx}`}
                aria-selected={isActive}
                aria-controls={`${testIdPrefix}-panel-${idx}`}
                tabIndex={isActive ? 0 : -1}
                data-testid={`${testIdPrefix}-tab-${idx}`}
                onClick={() => setActive(idx)}
                className={`w-full text-left flex items-center gap-4 py-5 transition-all duration-300 ${
                  isActive ? "text-ice" : "text-muted-foreground hover:text-ice/70"
                }`}
              >
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border text-sm font-mono font-semibold transition-colors duration-300 ${
                    isActive ? "bg-royal border-royal text-white" : "bg-surface border-ice/12 text-royal"
                  }`}
                >
                  {it.badge}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block font-display text-lg font-semibold leading-snug transition-all ${
                      isActive ? "translate-x-1" : ""
                    }`}
                  >
                    {it.title}
                  </span>
                  {it.subtitle && (
                    <span className="block mt-0.5 text-sm text-muted-foreground leading-snug">{it.subtitle}</span>
                  )}
                </span>
                <ArrowRight
                  size={16}
                  className={`ml-auto self-center shrink-0 transition-all ${
                    isActive ? "text-azure translate-x-1 lg:rotate-0 rotate-90" : "opacity-30"
                  }`}
                />
              </button>

              <AnimatePresence initial={false}>
                {isActive && (
                  <motion.div
                    key={`mobile-${idx}`}
                    id={`${testIdPrefix}-panel-${idx}`}
                    role="tabpanel"
                    aria-labelledby={`${testIdPrefix}-tab-${idx}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:hidden overflow-hidden"
                    data-testid={`${testIdPrefix}-mobile-panel-${idx}`}
                  >
                    <div className="pb-7 pl-[60px] pr-1">{it.render()}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Pannello desktop */}
      <div
        className="hidden lg:block bg-surface border border-ice/8 rounded-2xl p-6 sm:p-8 lg:p-12 min-h-[320px] relative overflow-hidden"
        role="tabpanel"
        aria-labelledby={`${testIdPrefix}-tab-${active}`}
        data-testid={`${testIdPrefix}-desktop-panel`}
      >
        <AnimatePresence mode="wait">
          {activeItem && (
            <motion.div
              key={active}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {activeItem.render()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
